import {
  CompanyEntity,
  NoticeCategory,
  NoticeKind,
  NoticeSeverity,
  PersonCapabilityType,
  RegionEntity,
  SiteEntity,
  toNoticeCategory,
  toNoticeKind,
  toNoticeSeverity
} from "@ots-frontend/ots-data";
import {UseFormReturnType} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Modal, ModalProps} from "@mantine/core";
import {useCallback} from "react";
import {NoticeFormProvider, useNoticeForm} from "./useNoticeForm";
import {NoticeFormFields} from "./NoticeFormFields";

export interface NoticeFormProps {
  options?: NoticeFormOptions;
  defaultValues?: Partial<NoticeFormValues>;
  onSubmit?: (values: NoticeFormValues) => void;
  submitting?: boolean;
}

export interface NoticeFormOptions {
  regionId?: string;
}

export interface NoticeFormValues {
  kind: NoticeKind
  category: NoticeCategory
  severity: NoticeSeverity
  title: string
  bodyText: string;
  expiresDate?: Date
  regions?: (RegionEntity | string)[]
  companies?: (CompanyEntity | string)[]
  audienceCapabilities?: (PersonCapabilityType | string)[]
}

export interface NoticeFormDefaultValues extends Partial<NoticeFormValues> {

}

export interface NoticeFormRawValues {
  kind?: string
  category?: string
  severity?: string
  title?: string
  bodyText?: string;
  expiresDate?: Date;
  regions?: string[]
  companies?: string[]
  audienceCapabilities?: string[]
}

export type NoticeFormType = UseFormReturnType<NoticeFormRawValues, (values: NoticeFormRawValues) => NoticeFormRawValues>

export const NoticeForm = (props: NoticeFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useNoticeForm({
    defaultValues: defaultValues,
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        kind: toNoticeKind(values.kind!),
        category: toNoticeCategory(values.category!),
        severity: toNoticeSeverity(values.severity!),
        title: values.title!,
        bodyText: values.bodyText,
        expiresDate: values.expiresDate,
        regions: values.regions,
        companies: values.companies,
        audienceCapabilities: values.audienceCapabilities,
      });
    }
  }, [form, onSubmit]);

  return (
      <NoticeFormProvider form={form}>
        <form onSubmit={form.onSubmit(onFormSubmit)}>
          <NoticeFormFields options={options}/>
          <Divider my="sm" variant="dotted"/>
          <Button type="submit" loading={submitting} fullWidth mt="xl">
            {t("common.action.submit", {ns: "common"})}
          </Button>
        </form>
      </NoticeFormProvider>
  );
};

interface NoticeFormModalProps extends NoticeFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const NoticeFormModal = (props: NoticeFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    submitting,
    ...rest
  } = props;

  return (
      <Modal {...rest}>
        <NoticeForm
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            submitting={submitting}/>
      </Modal>
  );
};

